import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { debugLog } from '../../helpers/debugLog';
import { MetadataService } from '../metadata/metadata';
import { QuizService } from '../quiz/quiz';
import { ReaderApi } from '../reader-api/reader-api';
import { ReaderNavigatorService } from '../readium/navigator/reader-navigator';

/**
 * Stops students from leaving a page with unanswered questions, as the previous NubeReader did.
 * With the "restrict next page with questions" reader permission a page turn opens the first
 * unanswered question instead and any other forward jump is refused with a warning. Without it the
 * student is asked to confirm, unless they chose not to be asked again. Sessions opened via a
 * shared-highlight link are exempt.
 */
@Injectable({
  providedIn: 'root',
})
export class QuestionsNavigationGuardService {
  // Same key as the previous NubeReader, so "don't show again" carries over between readers.
  private static readonly UNANSWERED_ALERT_KEY = 'questionsWithoutAnswer';

  private readonly metadataService = inject(MetadataService);
  private readonly quizService = inject(QuizService);
  private readonly readerApi = inject(ReaderApi);

  private readonly firstPendingQuestionOnPage = computed(
    () =>
      this.quizService
        .visibleQuizzes()
        .find(({ quiz, isPending }) => isPending && quiz.materialKind !== 'annotation')?.quiz,
  );

  private readonly notShowAlertMessages = linkedSignal(
    () => this.metadataService.metadata()?.userData.notShowAlertMessages ?? [],
  );

  private readonly pendingNavigation = signal<(() => void) | undefined>(undefined);

  readonly showWarning = signal(false);

  readonly showConfirmation = computed(() => this.pendingNavigation() !== undefined);

  constructor() {
    inject(ReaderNavigatorService).setForwardGuard((kind, proceed) =>
      this.allowForward(kind, proceed),
    );
  }

  allowForward(kind: 'pageTurn' | 'jump', proceed: () => void): boolean {
    if (this.metadataService.isTeacher()) return true;
    // A session opened via a shared link is only there to show that highlight: it never holds
    // the recipient's questions, and the guard would keep it from reaching the highlight.
    if (this.metadataService.sharedSelection()) return true;
    const pending = this.firstPendingQuestionOnPage();
    if (!pending) return true;

    if (this.metadataService.restrictNextPageWithQuestions()) {
      if (kind === 'pageTurn') {
        this.quizService.startAnswerQuiz(pending);
      } else {
        this.showWarning.set(true);
      }
      return false;
    }

    if (
      this.notShowAlertMessages().includes(QuestionsNavigationGuardService.UNANSWERED_ALERT_KEY)
    ) {
      return true;
    }
    this.pendingNavigation.set(proceed);
    return false;
  }

  dismissWarning(): void {
    this.showWarning.set(false);
  }

  confirmNavigation(dontAskAgain: boolean): void {
    const proceed = this.pendingNavigation();
    this.pendingNavigation.set(undefined);
    if (dontAskAgain) void this.stopAsking();
    proceed?.();
  }

  cancelNavigation(): void {
    this.pendingNavigation.set(undefined);
  }

  private async stopAsking(): Promise<void> {
    const messages = [
      ...this.notShowAlertMessages(),
      QuestionsNavigationGuardService.UNANSWERED_ALERT_KEY,
    ];
    this.notShowAlertMessages.set(messages);
    try {
      await this.readerApi.saveNotShowAlertMessages(messages);
    } catch (error) {
      debugLog('Could not save the unanswered questions preference', error);
    }
  }
}
